<template>
  <div v-if="!invalidType">
    <TopBanner
      layout="full-width"
      currentPage="Signup"
      :heading="pageHeading"
      bannerClass="banner-auth"
    />

    <section class="py-16 md:py-24 bg-slate-50/40">
      <div class="max-w-7xl mx-auto px-6 lg:px-10 xl:px-14">
        <div class="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          
          <!-- LEFT SIDE: Marketing/Partnership Pitch (Supplier, Agency, or User) -->
          <div class="lg:col-span-6 space-y-8 lg:pr-6 order-2 lg:order-1">
            <!-- Supplier Pitch -->
            <div v-if="type === 'supplier'" class="space-y-8">
              <div>
                <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-green-50 text-green-700 border border-green-200/50 uppercase tracking-widest">
                  Chauffeur &amp; Fleet Partners
                </span>
                <h2 class="text-3xl md:text-4xl font-extrabold text-slate-900 mt-4 leading-tight">
                  Tie Up With Us &amp; Grow Your Fleet Bookings
                </h2>
                <p class="text-slate-600 mt-4 text-[16px] leading-relaxed">
                  Vehitra World is a premium global ground transportation network. We partner with local, professional transport operators and car rental suppliers to deliver top-tier airport transfers and hourly travel services to global travelers.
                </p>
              </div>

              <!-- Features list -->
              <div class="space-y-6">
                <h3 class="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Why Partner With Us</h3>
                
                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-green-50 text-green-700 flex items-center justify-center border border-green-100 shadow-sm">
                    <i class="pi pi-chart-line text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Maximize Fleet Utilization</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Receive steady corporate transfer bookings, long-distance journeys, and hourly requests to fill empty leg runs.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-green-50 text-green-700 flex items-center justify-center border border-green-100 shadow-sm">
                    <i class="pi pi-globe text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Global Sales Reach</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Instantly gain bookings from our international agency partners and B2B corporate dashboard network.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-green-50 text-green-700 flex items-center justify-center border border-green-100 shadow-sm">
                    <i class="pi pi-wallet text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Transparent Settlements</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Get paid on time with direct bank transfers, consolidated monthly Statement of Account (SOA) reporting, and clear ledger entries.</p>
                  </div>
                </div>
              </div>

              <!-- Steps -->
              <div class="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 class="text-sm uppercase tracking-wider font-bold text-slate-850">Onboarding Process</h3>
                <ol class="relative border-l border-emerald-100 ml-3 space-y-4 text-sm text-slate-600">
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Submit Proposal Form:</strong> Share details of your vehicle categories and message.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Fleet &amp; Service Review:</strong> Our operations team reviews your fleet size, age, and insurance details.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Integration &amp; Rates Setup:</strong> Upload your rates, verify details on the partner panel, and start accepting jobs.
                  </li>
                </ol>
              </div>
            </div>

            <!-- Agency Pitch -->
            <div v-if="type === 'agent'" class="space-y-8">
              <div>
                <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/50 uppercase tracking-widest">
                  Travel Agency Partner
                </span>
                <h2 class="text-3xl md:text-4xl font-extrabold text-slate-900 mt-4 leading-tight">
                  Grow Your Travel Agency Margins &amp; Booking Reach
                </h2>
                <p class="text-slate-600 mt-4 text-[16px] leading-relaxed">
                  Partner with Vehitra World to access our premium global ground transportation inventory. Manage bookings, set commissions/markups, earn loyalty points, and service clients with premium airport transfers and chauffeur services globally.
                </p>
              </div>

              <!-- Features list -->
              <div class="space-y-6">
                <h3 class="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Agency Benefits</h3>
                
                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                    <i class="pi pi-globe text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Global Service Network</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Instantly book airport transfers, hourly rentals, and point-to-point chauffeur rides in over 100+ countries.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                    <i class="pi pi-gift text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Earn Loyalty Points &amp; Markups</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Accumulate loyalty points on every confirmed trip and redeem points directly as credits in your agency wallet.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                    <i class="pi pi-users text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Sub-Agent &amp; Staff Control</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Create and manage accounts for your sub-agents, monitor bookings, set customized rights, and view performance charts.</p>
                  </div>
                </div>
              </div>

              <!-- Steps -->
              <div class="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 class="text-sm uppercase tracking-wider font-bold text-slate-850">Onboarding Process</h3>
                <ol class="relative border-l border-emerald-100 ml-3 space-y-4 text-sm text-slate-600">
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Register Your Agency:</strong> Complete the signup form with CR number and company details.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Account Activation:</strong> Our partnerships team verifies and activates your B2B account within 24 hours.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500"></span>
                    <strong>Book &amp; Earn:</strong> Access net rates, set agent markups, and earn loyalty points on every booking.
                  </li>
                </ol>
              </div>
            </div>

            <!-- User/Corporate Pitch (Premium & Trust) -->
            <div v-if="type === 'web' || type === 'corporate'" class="space-y-8">
              <div>
                <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/50 uppercase tracking-widest">
                  Premium Chauffeur Service
                </span>
                <h2 class="text-3xl md:text-4xl font-extrabold text-slate-900 mt-4 leading-tight">
                  Experience Seamless Global Travel
                </h2>
                <p class="text-slate-600 mt-4 text-[16px] leading-relaxed">
                  Vehitra World is a premium global ground transportation platform. We provide highly reliable airport transfers, point-to-point transfers, and hourly chauffeur services for discerning business and leisure travelers in hundreds of major cities.
                </p>
              </div>

              <!-- Features list -->
              <div class="space-y-6">
                <h3 class="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Our Customer Promise</h3>
                
                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center border border-blue-100 shadow-sm">
                    <i class="pi pi-shield text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Uncompromised Safety &amp; Quality</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">Every chauffeur is licensed, English-speaking, fully vetted, and drives a late-model premium vehicle.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center border border-blue-100 shadow-sm">
                    <i class="pi pi-clock text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Flight Monitoring &amp; Wait Time</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">We track your flight in real time for delays. All airport pickups include 60 minutes of complimentary waiting time.</p>
                  </div>
                </div>

                <div class="flex gap-4">
                  <div class="shrink-0 h-12 w-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center border border-blue-100 shadow-sm">
                    <i class="pi pi-ticket text-lg"></i>
                  </div>
                  <div>
                    <h4 class="text-[16px] font-bold text-slate-800">Fixed &amp; All-Inclusive Prices</h4>
                    <p class="text-sm text-slate-500 mt-1 leading-relaxed">No hidden fees, surprise charges, or fuel/toll surcharges. The price quoted is exactly what you pay.</p>
                  </div>
                </div>
              </div>

              <!-- Steps -->
              <div class="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm space-y-4">
                <h3 class="text-sm uppercase tracking-wider font-bold text-slate-850">How it works</h3>
                <ol class="relative border-l border-blue-100 ml-3 space-y-4 text-sm text-slate-600">
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                    <strong>Create Your Account:</strong> Save your personal information and profile preferences securely.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                    <strong>Book ground travel:</strong> Enter your pickup/dropoff points, select a vehicle, and complete the reservation.
                  </li>
                  <li class="pl-4 relative">
                    <span class="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                    <strong>Travel in comfort:</strong> Meet your professional chauffeur and reach your destination safely.
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <!-- RIGHT SIDE: The Form Card (Exactly original fields, classes and layout) -->
          <div class="lg:col-span-6 w-full order-1 lg:order-2">
            <!-- Glass card -->
            <div
              class="rounded-2xl bg-white/80 supports-[backdrop-filter]:backdrop-blur-md
                     shadow-xl ring-1 ring-black/5 p-6 md:p-10"
            >
              <!-- Header -->
              <header class="mb-8">
                <h1 class="text-slate-900 text-2xl md:text-3xl font-semibold tracking-tight">
                  {{ pageHeading }}
                </h1>
                <p class="text-slate-500 mt-1 text-[14px]">
                  Fill in your details below to get started.
                </p>
              </header>

              <!-- FORM -->
              <div class="space-y-10">
                <!-- Section: Basic Info -->
                <section>
                  <h2 class="section-title">Basic information</h2>

                  <!-- Grid: two columns on desktop -->
                  <div class="grid md:grid-cols-2 gap-5 md:gap-6">
                    <!-- Full name -->
                    <div class="col-span-2 md:col-span-1">
                      <label for="signup-fullname" class="label-upper" :class="form.fullName.error ? 'text-ef-text' : 'text-slate-500'">
                        Full name *
                      </label>
                      <div class="field-wrap" :class="form.fullName.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.fullName.value" type="text" id="signup-fullname" class="field-input" placeholder="John Smith" />
                      </div>
                      <p v-if="form.fullName.error" class="error-msg">{{ form.fullName.errorMessage }}</p>
                    </div>

                    <!-- Email -->
                    <div class="col-span-2 md:col-span-1">
                      <label for="signup-email" class="label-upper" :class="form.email.error ? 'text-ef-text' : 'text-slate-500'">
                        Email *
                      </label>
                      <div class="field-wrap" :class="form.email.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.email.value" type="email" id="signup-email" class="field-input" placeholder="you@example.com" />
                      </div>
                      <p v-if="form.email.error" class="error-msg">{{ form.email.errorMessage }}</p>
                    </div>

                    <!-- Company name (not for web) -->
                    <div v-if="type !== 'web'" class="col-span-2 md:col-span-1">
                      <label for="signup-companyname" class="label-upper" :class="form.companyName.error ? 'text-ef-text' : 'text-slate-500'">
                        Company name *
                      </label>
                      <div class="field-wrap" :class="form.companyName.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.companyName.value" type="text" id="signup-companyname" class="field-input" placeholder="Acme LLC" />
                      </div>
                      <p v-if="form.companyName.error" class="error-msg">{{ form.companyName.errorMessage }}</p>
                    </div>

                    <!-- Phone (not for web) -->
                    <div v-if="type !== 'web'" class="col-span-2 md:col-span-1">
                      <label for="signup-phonenumber" class="label-upper" :class="form.phone.error ? 'text-ef-text' : 'text-slate-500'">
                        Phone Number *
                      </label>
                      <div class="field-wrap" :class="form.phone.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <div class="relative w-full">
                          <CountrySearchablePhoneField
                            @phoneNumber="handlePhoneInput"
                            class="h-[48px] md:h-[52px] font-semibold text-[15px] px-2"
                          />
                        </div>
                      </div>
                      <p v-if="form.phone.error" class="error-msg">{{ form.phone.errorMessage }}</p>
                    </div>

                    <!-- Agent-only: CR -->
                    <div v-if="type === 'agent'" class="col-span-2 md:col-span-1">
                      <label for="signup-cr" class="label-upper" :class="form.crNumber.error ? 'text-ef-text' : 'text-slate-500'">
                        CR Number *
                      </label>
                      <div class="field-wrap" :class="form.crNumber.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.crNumber.value" type="text" id="signup-cr" class="field-input" placeholder="123456" />
                      </div>
                      <p v-if="form.crNumber.error" class="error-msg">{{ form.crNumber.errorMessage }}</p>
                    </div>

                    <!-- Agent-only: VAT (optional) -->
                    <div v-if="type === 'agent'" class="col-span-2 md:col-span-1">
                      <label for="signup-vat" class="label-upper" :class="form.vat.error ? 'text-ef-text' : 'text-slate-500'">
                        VAT (optional)
                      </label>
                      <div class="field-wrap" :class="form.vat.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.vat.value" type="text" id="signup-vat" class="field-input" placeholder="(if applicable)" />
                      </div>
                      <p v-if="form.vat.error" class="error-msg">{{ form.vat.errorMessage }}</p>
                    </div>

                    <!-- Agent-only: Address -->
                    <div v-if="type === 'agent'" class="col-span-2">
                      <label for="signup-address" class="label-upper" :class="form.address.error ? 'text-ef-text' : 'text-slate-500'">
                        Address *
                      </label>
                      <div class="field-wrap" :class="form.address.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input v-model="form.address.value" type="text" id="signup-address" class="field-input" placeholder="Street, City, Country" />
                      </div>
                      <p v-if="form.address.error" class="error-msg">{{ form.address.errorMessage }}</p>
                    </div>

                    <!-- Supplier message -->
                    <div v-if="type === 'supplier'" class="col-span-2">
                      <label for="signup-message" class="label-upper text-slate-500">
                        Message *
                      </label>
                      <div class="field-wrap border-slate-200">
                        <textarea
                          v-model="form.message.value"
                          id="signup-message"
                          class="field-input h-[180px] md:h-[220px] resize-y"
                          placeholder="Tell us more about your services…"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Section: Security (not for supplier) -->
                <section v-if="type !== 'supplier'">
                  <h2 class="section-title">Security</h2>
                  <div class="grid md:grid-cols-2 gap-5 md:gap-6">
                    <!-- Password -->
                    <div class="col-span-2 md:col-span-1">
                      <label for="signup-password" class="label-upper" :class="form.password.error ? 'text-ef-text' : 'text-slate-500'">
                        Password *
                      </label>
                      <div class="field-wrap pr-1" :class="form.password.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input
                          v-model="form.password.value"
                          :type="passwordFieldtype"
                          id="signup-password"
                          class="field-input"
                          placeholder="••••••••"
                        />
                        <button type="button" class="icon-btn" @click="togglePasswordFieldType">
                          <font-awesome-icon :icon="passwordFieldtype === 'password' ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'" />
                        </button>
                      </div>
                      <p v-if="form.password.error" class="error-msg">{{ form.password.errorMessage }}</p>
                    </div>

                    <!-- Confirm Password -->
                    <div class="col-span-2 md:col-span-1">
                      <label for="signup-password-confirmation" class="label-upper" :class="form.passwordConfirmation.error ? 'text-ef-text' : 'text-slate-500'">
                        Confirm Password *
                      </label>
                      <div class="field-wrap pr-1" :class="form.passwordConfirmation.error ? 'border-ef-outline ring-ef-outline' : 'border-slate-200'">
                        <input
                          v-model="form.passwordConfirmation.value"
                          :type="passwordFieldtype"
                          id="signup-password-confirmation"
                          class="field-input"
                          placeholder="••••••••"
                        />
                        <button type="button" class="icon-btn" @click="togglePasswordFieldType">
                          <font-awesome-icon :icon="passwordFieldtype === 'password' ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'" />
                        </button>
                      </div>
                      <p v-if="form.passwordConfirmation.error" class="error-msg">
                        {{ form.passwordConfirmation.errorMessage }}
                      </p>
                    </div>
                  </div>
                </section>

                <!-- Actions -->
                <section class="space-y-4">
                  <div class="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                    <button
                      v-if="type !== 'supplier'"
                      type="button"
                      @click="$router.push({ name: 'Login' })"
                      class="order-2 md:order-1 w-full md:w-auto rounded-full px-5 md:px-7 py-3
                             border-2 border-slate-200 text-slate-700 hover:bg-white
                             uppercase tracking-wide text-xs md:text-[12px] font-medium
                             transition"
                    >
                      I already have an account
                    </button>

                    <button
                      type="button"
                      @click="handleSignup"
                      :disabled="isLoading"
                      class="order-1 md:order-2 w-full md:w-auto rounded-full px-6 md:px-8 py-3
                             border-2 border-emerald-600 bg-emerald-600 text-white
                             uppercase tracking-wide text-xs md:text-[12px] font-semibold
                             shadow hover:bg-white hover:text-emerald-700
                             transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span v-if="!isLoading">{{ type === 'supplier' ? 'Submit' : 'Signup' }}</span>
                      <span v-else class="inline-flex items-center gap-2">
                        <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2a10 10 0 100 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        Processing…
                      </span>
                    </button>
                  </div>

                  <!-- reCAPTCHA -->
                  <div class="flex flex-col justify-end gap-2">
                    <div class="mt-2 ml-auto google-recaptcha"></div>
                    <p v-if="recaptchaError" class="error-msg ml-auto">{{ recaptchaError }}</p>
                  </div>
                </section>
              </div>
            </div>

            <p class="text-center text-slate-500 text-sm mt-5" v-if="type !== 'supplier'">
              Need help? <span class="text-emerald-700">Our team is available 24/7</span>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <Notification
      :show="notif.show"
      :type="notif.type"
      :message="notif.message"
      :messageDetails="notif.messageDetails"
      @closed="notifClosed"
    />
  </div>

  <div v-else>
    <Notification
      :show="notif.show"
      type="error"
      message="We do not recognize this url"
      messageDetails="The url you are visiting is invalid and cannot be used to display anything"
      @closed="notifClosed"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, toRef } from 'vue'
import TopBanner from '@/components/TopBanner.vue'
import CountrySearchablePhoneField from '@/components/CountrySearchablePhoneField.vue'
import { handleApiError, isValidEmail, loadScript } from '@/utils/common'
import Auth from '@/composables/Auth'
import { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'
// @ts-ignore
import $ from 'jquery'

const props = defineProps<{ type: 'web'|'agent'|'supplier'|'corporate' }>()
const type = toRef(props, 'type')              // <- expose `type` for the template

// const { registerSupplier, registerAgent, registerUser }  = Auth()

const isLoading = ref(false)
const invalidType = ref(false)
const recaptchaError = ref<any>(null)
const passwordFieldtype = ref<'password' | 'text'>('password')
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})

const pageHeading = computed(() => {
  if (type.value === 'agent') return 'Create Your Agency Account'
  if (type.value === 'web' || type.value === 'corporate') return 'Create Your Account'
  if (type.value === 'supplier') return 'Create Your Supplier Account'
  return 'Signup'
})

const form = ref<any>({
  fullName: { value: '', error: false, errorMessage: '' },
  companyName: { value: '', error: false, errorMessage: '' },
  email: { value: '', error: false, errorMessage: '' },
  phone: { value: '', error: false, errorMessage: '' },
  password: { value: '', error: false, errorMessage: '' },
  passwordConfirmation: { value: '', error: false, errorMessage: '' },
  message: { value: '', error: false, errorMessage: '' },
  crNumber: { value: '', error: false, errorMessage: '' },
  vat: { value: '', error: false, errorMessage: '' },
  address: { value: '', error: false, errorMessage: '' },
})

const togglePasswordFieldType = () => {
  passwordFieldtype.value = passwordFieldtype.value === 'password' ? 'text' : 'password'
}

const handlePhoneInput = (input: any) => {
  if (input === false) {
    form.value.phone.value = ''
    form.value.phone.error = true
    form.value.phone.errorMessage = 'Phone is required'
  } else {
    form.value.phone.value = input
    form.value.phone.error = false
  }
}

const validateForm = (): boolean => {
  let error = false
  const recaptcha = $('textarea[name="g-recaptcha-response"]')

  if (!recaptcha.length || recaptcha.val() == '' || !recaptcha.val()) {
    error = true
    recaptchaError.value = 'Please validate the recaptcha'
  } else {
    recaptchaError.value = null
  }

  if (form.value.fullName.value === '') {
    form.value.fullName.error = true
    form.value.fullName.errorMessage = 'Full name is required'
    error = true
  }

  if (form.value.email.value === '') {
    form.value.email.error = true
    form.value.email.errorMessage = 'Email is required'
    error = true
  } else if (!isValidEmail(form.value.email.value)) {
    form.value.email.error = true
    form.value.email.errorMessage = 'Email is invalid'
    error = true
  }

  if (type.value !== 'web') {
    if (form.value.phone.value === '') {
      form.value.phone.error = true
      form.value.phone.errorMessage = 'Phone number is required'
      error = true
    } else if (form.value.phone.value.length < 11) {
      form.value.phone.error = true
      form.value.phone.errorMessage = 'Phone number is invalid'
      error = true
    }

    if (form.value.companyName.value === '') {
      form.value.companyName.error = true
      form.value.companyName.errorMessage = 'Company name is required'
      error = true
    }
  }

  if (type.value !== 'supplier') {
    if (form.value.password.value === '') {
      form.value.password.error = true
      form.value.password.errorMessage = 'Password is required'
      error = true
    } else if (form.value.password.value.length < 6) {
      form.value.password.error = true
      form.value.password.errorMessage = 'Password must be at least 6 characters long'
      error = true
    }

    if (form.value.passwordConfirmation.value === '') {
      form.value.passwordConfirmation.error = true
      form.value.passwordConfirmation.errorMessage = 'Password confirmation is required'
      error = true
    } else if (form.value.passwordConfirmation.value !== form.value.password.value) {
      form.value.password.error = true
      form.value.passwordConfirmation.error = true
      form.value.password.errorMessage = 'Password does not match with password confirmation'
      error = true
    }
  }

  if (type.value === 'agent') {
    if (form.value.crNumber.value === '') {
      form.value.crNumber.error = true
      form.value.crNumber.errorMessage = 'CR Number is required'
      error = true
    }
    if (form.value.address.value === '') {
      form.value.address.error = true
      form.value.address.errorMessage = 'Address is required'
      error = true
    }
  }

  return error
}

const resetFormErrors = () => {
  Object.keys(form.value).forEach((key: string) => {
    form.value[key].error = false
    form.value[key].errorMessage = ''
  })
}

const resetForm = () => {
  Object.keys(form.value).forEach((key: string) => {
    form.value[key].value = ''
  })
}

const { registerSupplier, registerAgent, registerAgentRequest, registerUser }  = Auth()

const handleSignup = async () => {
  resetFormErrors()
  const error = validateForm()
  if (error || isLoading.value) return

  isLoading.value = true
  const recaptcha = $('textarea[name="g-recaptcha-response"]').val()
  const origin = window.location.origin

  try {
    let response: any

    if (type.value === 'supplier') {
      response = await registerSupplier(
        form.value.fullName.value,
        form.value.companyName.value,
        form.value.email.value,
        form.value.phone.value,
        form.value.message.value,
        recaptcha
      )
    } else if (type.value === 'agent') {
      response = await registerAgentRequest({
        name: form.value.fullName.value,
        email: form.value.email.value,
        password: form.value.password.value,
        passwordConfirmation: form.value.passwordConfirmation.value,
        phoneNumber: form.value.phone.value,
        crNumber: form.value.crNumber.value,
        address: form.value.address.value,
        vatNumber: form.value.vat.value || null,
        companyName: form.value.companyName.value || '',
        type: 'agent',
        origin,
        recaptcha
      })
    } else {
      response = await registerUser(
        form.value.fullName.value,
        form.value.email.value,
        form.value.phone.value,
        form.value.companyName.value,
        form.value.password.value,
        form.value.passwordConfirmation.value,
        type.value,
        recaptcha
      )
    }

    if (response.status === 201) {
      let messageDetails = ''
      if (type.value === 'supplier') {
        messageDetails = 'We have notified our support team. You will hear from us soon'
      } else if (type.value === 'agent') {
        messageDetails = 'Your Travel Agency registration has been submitted and is pending administrative review. You will receive an email once approved.'
      } else {
        messageDetails = 'You have created an account with us. Please check your email to activate your account'
      }
      notif.value = { show: true, type: 'success', message: 'Success', messageDetails }
      setTimeout(() => { notifClosed() }, 5000)
      resetForm()
    } else {
      const err = handleApiError(response)
      notif.value = { show: true, type: 'error', message: err, messageDetails: '' }
    }
  } finally {
    isLoading.value = false
  }
}

const notifClosed = () => { notif.value.show = false }

onMounted(() => {
  const recaptchaScriptUrl = 'https://www.google.com/recaptcha/api.js'
  $('.google-recaptcha').html('<div class="g-recaptcha" data-sitekey="6LdgLVspAAAAAH4Y109CAIfw26hm_onZFQsycv1v"></div>')
  loadScript(recaptchaScriptUrl, function () {})

  if (type.value !== 'agent' && type.value !== 'supplier' && type.value !== 'web') {
    invalidType.value = true
    notif.value.show = true
  }
})
</script>

<style scoped>
/* Section heading */
.section-title {
  @apply text-slate-800 font-semibold text-[15px] uppercase tracking-[0.18em] mb-4;
}

/* Labels */
.label-upper {
  @apply block text-[12px] uppercase tracking-widest;
}

/* Input wrappers */
.field-wrap {
  @apply mt-2 flex items-center rounded-xl border bg-white/95
         focus-within:ring-2 focus-within:ring-emerald-500/25
         focus-within:border-emerald-400 transition;
}

/* Inputs / Textareas */
.field-input {
  @apply w-full h-[48px] md:h-[52px] bg-transparent outline-none
         text-slate-800 placeholder-slate-400 px-3 text-[15px];
}
textarea.field-input {
  @apply h-auto py-3;
}

/* Eye button */
.icon-btn {
  @apply ml-auto mr-1 inline-flex items-center justify-center h-10 w-10
         rounded-lg text-slate-500 hover:text-slate-700 hover:bg-white/70 transition;
}

/* Error styles */
.text-ef-text { color: #b91c1c; }
.border-ef-outline { border-color: #fca5a5 !important; }
.ring-ef-outline { box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2) inset; }
.text-ef-error { color: #dc2626; }
.error-msg { @apply text-[13px] mt-1 text-ef-error; }

/* Country phone field tweaks */
:deep(input[type="tel"]) {
  @apply w-full bg-transparent outline-none text-slate-800 placeholder-slate-400;
}
</style>